import { useState, useRef, useEffect, useCallback } from 'react';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import {
  FONT_DEFS, OVERLAY_DEFAULTS, ensureFontsLoaded, drawTextOverlay, measureOverlay,
} from '../../utils/textOverlayRenderer';
import { flattenAndSave, flattenVideoAndSave } from '../../services/storyService';
import { exportVideoStory } from '../../utils/storyVideoExporter';

const isVideoUrl = (url) => /\.(mp4|mov|webm|m4v)(\?|$)/i.test(url || '');

// Story canvas is authored at 1080×1920 (9:16) — the same frame the renderer scales text against.
const STAGE_W = 1080;
const STAGE_H = 1920;
const REST_TMS = 10_000_000; // render overlays at their animation rest state (still image)

const SOLIDS = ['#111827', '#ffffff', '#7c3aed', '#db2777', '#f97316', '#2563eb', '#059669', '#000000'];
const GRADIENTS = [
  { from: '#8b5cf6', to: '#ec4899' },
  { from: '#f97316', to: '#db2777' },
  { from: '#2563eb', to: '#7c3aed' },
  { from: '#059669', to: '#2563eb' },
  { from: '#111827', to: '#4b5563' },
];
const COLORS = ['#ffffff', '#111827', '#f43f5e', '#f59e0b', '#22c55e', '#3b82f6', '#a855f7', '#000000'];

const newLayer = () => ({
  ...OVERLAY_DEFAULTS,
  id: `t-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  text: 'Tap to edit',
  fontSize: 56,
  x: 0.5,
  y: 0.5,
  animation: 'none',
  wrap: true, // wrap long text to the 9:16 frame instead of overflowing
});

// Visual stickers are just emoji overlay layers — the renderer falls back to the system emoji font,
// so they draw (and flatten into the image/video) exactly like text. Interactive mention/link/poll
// stickers aren't offered: the Facebook/Instagram Stories publish API is media-only.
const STICKERS = ['🔥', '❤️', '😂', '✨', '🎉', '👍', '😍', '🙌', '💯', '⭐️', '📍', '🎵', '➡️', '👀', '💥', '🥳', '☀️', '🌈', '👑', '💬'];

const newSticker = (emoji) => ({
  ...OVERLAY_DEFAULTS,
  id: `s-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  kind: 'sticker',
  text: emoji,
  fontSize: 90,
  shadow: null,
  stroke: null,
  x: 0.5,
  y: 0.5,
  animation: 'none',
});

/**
 * Story Studio — a standalone 9:16 editor (background + text + fonts) that flattens to a single
 * image. It reuses only the pure overlay renderer; it does NOT touch the video editor pipeline.
 */
export default function StoryStudio({ item, onClose, onSaved }) {
  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const videoMode = isVideoUrl(item?.s3Url);
  const [bg, setBg] = useState({ type: 'gradient', gradient: GRADIENTS[0], color: '#111827' });
  const [layers, setLayers] = useState([newLayer()]);
  const [selectedId, setSelectedId] = useState(null);
  const [photo, setPhoto] = useState(null);     // HTMLImageElement for a photo background (image mode)
  const [fontsReady, setFontsReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const dragging = useRef(false);

  const selected = layers.find((l) => l.id === selectedId) || null;
  const hasPhoto = Boolean(item?.s3Url) && !videoMode;

  useEffect(() => { ensureFontsLoaded().finally(() => setFontsReady(true)); }, []);

  // Load the uploaded photo (if any) for use as a background. Needs CORS to export without tainting.
  useEffect(() => {
    if (!hasPhoto) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => setPhoto(img);
    img.onerror = () => { /* fall back to colour/gradient backgrounds */ };
    img.src = item.s3Url;
  }, [item?.s3Url, hasPhoto]);

  const paintBackground = useCallback((ctx) => {
    if (bg.type === 'photo' && photo) {
      // cover-fit the photo into 9:16
      const scale = Math.max(STAGE_W / photo.width, STAGE_H / photo.height);
      const w = photo.width * scale;
      const h = photo.height * scale;
      ctx.drawImage(photo, (STAGE_W - w) / 2, (STAGE_H - h) / 2, w, h);
    } else if (bg.type === 'gradient') {
      const g = ctx.createLinearGradient(0, 0, STAGE_W, STAGE_H);
      g.addColorStop(0, bg.gradient.from);
      g.addColorStop(1, bg.gradient.to);
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, STAGE_W, STAGE_H);
    } else {
      ctx.fillStyle = bg.color;
      ctx.fillRect(0, 0, STAGE_W, STAGE_H);
    }
  }, [bg, photo]);

  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, STAGE_W, STAGE_H);
    // In video mode the <video> element shows through behind this canvas, so we paint only the text.
    if (!videoMode) paintBackground(ctx);
    const box = { x: 0, y: 0, w: STAGE_W, h: STAGE_H };
    layers.forEach((l) => drawTextOverlay(ctx, l, box, REST_TMS));
    // selection outline
    if (selected) {
      const m = measureOverlay(ctx, selected, box);
      ctx.save();
      ctx.strokeStyle = 'rgba(255,255,255,0.9)';
      ctx.lineWidth = 3;
      ctx.setLineDash([12, 10]);
      ctx.strokeRect(m.cx - m.blockW / 2 - m.pad, m.cy - m.blockH / 2 - m.pad,
        m.blockW + m.pad * 2, m.blockH + m.pad * 2);
      ctx.restore();
    }
  }, [layers, paintBackground, selected, videoMode]);

  useEffect(() => { render(); }, [render, fontsReady]);

  // ── Layer editing ──────────────────────────────────────────────────────────
  const updateSelected = (patch) =>
    setLayers((ls) => ls.map((l) => (l.id === selectedId ? { ...l, ...patch } : l)));
  const addLayer = () => { const l = newLayer(); setLayers((ls) => [...ls, l]); setSelectedId(l.id); };
  const addSticker = (emoji) => { const s = newSticker(emoji); setLayers((ls) => [...ls, s]); setSelectedId(s.id); };
  const removeSelected = () => { setLayers((ls) => ls.filter((l) => l.id !== selectedId)); setSelectedId(null); };

  // ── Drag the selected layer on the canvas ───────────────────────────────────
  const toStageXY = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  };
  const onPointerDown = (e) => {
    const ctx = canvasRef.current.getContext('2d');
    const box = { x: 0, y: 0, w: STAGE_W, h: STAGE_H };
    const p = toStageXY(e);
    const px = p.x * STAGE_W;
    const py = p.y * STAGE_H;
    // hit-test topmost layer
    const hit = [...layers].reverse().find((l) => {
      const m = measureOverlay(ctx, l, box);
      return Math.abs(px - m.cx) <= m.blockW / 2 + m.pad && Math.abs(py - m.cy) <= m.blockH / 2 + m.pad;
    });
    if (hit) { setSelectedId(hit.id); dragging.current = true; canvasRef.current.setPointerCapture(e.pointerId); }
    else setSelectedId(null);
  };
  const onPointerMove = (e) => {
    if (!dragging.current || !selectedId) return;
    const p = toStageXY(e);
    updateSelected({ x: Math.max(0, Math.min(1, p.x)), y: Math.max(0, Math.min(1, p.y)) });
  };
  const onPointerUp = () => { dragging.current = false; };

  const save = async () => {
    setSaving(true);
    try {
      await ensureFontsLoaded();
      setSelectedId(null); // drop the selection outline from the exported frame
      // allow the deselect re-render to paint before exporting
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      let updated;
      if (videoMode) {
        const blob = await exportVideoStory(videoRef.current, layers, {
          background: bg.type === 'solid' ? bg : undefined,
          onProgress: setProgress,
        });
        updated = await flattenVideoAndSave(item.id, blob, { onProgress: setProgress });
      } else {
        updated = await flattenAndSave(item.id, canvasRef.current);
      }
      toast.success('Story saved');
      onSaved?.(updated);
    } catch (err) {
      toast.error(err?.message || 'Could not save the story');
    } finally {
      setSaving(false);
      setProgress(0);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 flex flex-col" role="dialog" aria-modal="true">
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <h3 className="font-display font-bold text-lg">Design story</h3>
        <div className="flex items-center gap-2">
          <button onClick={save} disabled={saving} className="btn btn-primary btn-sm">
            {saving ? (videoMode && progress > 0 ? `Rendering ${progress}%` : 'Saving…') : 'Save story'}
          </button>
          <button onClick={onClose} aria-label="Close" className="p-2 rounded-lg hover:bg-white/10"><XMarkIcon className="w-5 h-5" /></button>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col md:flex-row gap-4 px-4 pb-4 overflow-auto">
        {/* Stage — in video mode the <video> shows behind a transparent text canvas */}
        <div className="flex-1 min-h-0 flex items-center justify-center">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-black"
            style={{ height: 'min(72vh, 640px)', aspectRatio: '9 / 16', maxWidth: '100%' }}>
            {videoMode && (
              <video
                ref={videoRef}
                src={item.s3Url}
                crossOrigin="anonymous"
                muted playsInline loop autoPlay
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
            <canvas
              ref={canvasRef}
              width={STAGE_W}
              height={STAGE_H}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              className="absolute inset-0 w-full h-full touch-none"
            />
          </div>
        </div>

        {/* Controls */}
        <div className="w-full md:w-80 flex-shrink-0 space-y-4 text-sm overflow-y-auto">
          {/* Background — image stories only (a video story fills the frame) */}
          {!videoMode && (
          <div className="card p-3">
            <p className="label mb-2">Background</p>
            <div className="flex flex-wrap gap-2 mb-2">
              {hasPhoto && (
                <button onClick={() => setBg((b) => ({ ...b, type: 'photo' }))}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${bg.type === 'photo' ? 'border-violet-500 text-violet-600' : 'border-gray-200 dark:border-dark-600'}`}>Photo</button>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {GRADIENTS.map((g, i) => (
                <button key={i} onClick={() => setBg({ type: 'gradient', gradient: g })}
                  aria-label="gradient" className="w-7 h-7 rounded-md border border-black/10"
                  style={{ background: `linear-gradient(135deg, ${g.from}, ${g.to})` }} />
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {SOLIDS.map((c) => (
                <button key={c} onClick={() => setBg({ type: 'solid', color: c })}
                  aria-label={`color ${c}`} className="w-7 h-7 rounded-md border border-black/10" style={{ background: c }} />
              ))}
            </div>
          </div>
          )}

          {/* Layers */}
          <div className="card p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="label mb-0">Text</p>
              <button onClick={addLayer} className="btn btn-secondary btn-sm inline-flex items-center gap-1"><PlusIcon className="w-4 h-4" />Add</button>
            </div>
            <div className="space-y-1">
              {layers.map((l) => (
                <button key={l.id} onClick={() => setSelectedId(l.id)}
                  className={`w-full text-left px-2.5 py-1.5 rounded-lg truncate ${l.id === selectedId ? 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300' : 'hover:bg-gray-50 dark:hover:bg-dark-700'}`}>
                  {l.text || '(empty)'}
                </button>
              ))}
              {layers.length === 0 && <p className="text-xs text-gray-400 px-1">No text yet — add a layer.</p>}
            </div>
          </div>

          {/* Stickers */}
          <div className="card p-3">
            <p className="label mb-2">Stickers</p>
            <div className="flex flex-wrap gap-1">
              {STICKERS.map((e) => (
                <button key={e} onClick={() => addSticker(e)} aria-label={`sticker ${e}`}
                  className="w-9 h-9 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 text-2xl leading-none flex items-center justify-center">
                  {e}
                </button>
              ))}
            </div>
          </div>

          {/* Selected layer editor */}
          {selected && selected.kind === 'sticker' && (
            <div className="card p-3 space-y-3">
              <div className="flex items-center justify-between">
                <p className="label mb-0">Edit sticker</p>
                <button onClick={removeSelected} aria-label="Delete sticker" className="p-1 text-gray-400 hover:text-red-500"><TrashIcon className="w-4 h-4" /></button>
              </div>
              <div className="text-center text-4xl leading-none py-1">{selected.text}</div>
              <div>
                <p className="label">Size</p>
                <input type="range" min="40" max="240" value={selected.fontSize}
                  onChange={(e) => updateSelected({ fontSize: Number(e.target.value) })} className="w-full" />
              </div>
              <p className="text-xs text-gray-400">Tip: drag the sticker on the canvas to reposition it.</p>
            </div>
          )}

          {selected && selected.kind !== 'sticker' && (
            <div className="card p-3 space-y-3">
              <div className="flex items-center justify-between">
                <p className="label mb-0">Edit text</p>
                <button onClick={removeSelected} aria-label="Delete layer" className="p-1 text-gray-400 hover:text-red-500"><TrashIcon className="w-4 h-4" /></button>
              </div>
              <textarea className="input resize-none" rows={2} value={selected.text}
                onChange={(e) => updateSelected({ text: e.target.value })} placeholder="Your text" />
              <div>
                <p className="label">Font</p>
                <select className="input" value={selected.fontFamily} onChange={(e) => updateSelected({ fontFamily: e.target.value })}>
                  {Object.entries(FONT_DEFS).map(([k, f]) => <option key={k} value={k}>{f.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="label">Size</p>
                  <input type="range" min="24" max="140" value={selected.fontSize}
                    onChange={(e) => updateSelected({ fontSize: Number(e.target.value) })} className="w-full" />
                </div>
                <div>
                  <p className="label">Align</p>
                  <div className="flex gap-1">
                    {['left', 'center', 'right'].map((a) => (
                      <button key={a} onClick={() => updateSelected({ align: a })}
                        className={`flex-1 px-1 py-1 rounded text-xs capitalize border ${selected.align === a ? 'border-violet-500 text-violet-600' : 'border-gray-200 dark:border-dark-600'}`}>{a}</button>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <p className="label">Colour</p>
                <div className="flex flex-wrap gap-1.5">
                  {COLORS.map((c) => (
                    <button key={c} onClick={() => updateSelected({ color: c, gradient: null })}
                      aria-label={`text ${c}`} className={`w-6 h-6 rounded-md border ${selected.color === c && !selected.gradient ? 'ring-2 ring-violet-500' : 'border-black/10'}`} style={{ background: c }} />
                  ))}
                </div>
              </div>
              <p className="text-xs text-gray-400">Tip: drag the text on the canvas to reposition it.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
